import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { BackButton } from "../../pages/MatterDetails/MatterDetailsStyled";
import { X } from "@phosphor-icons/react";
import RecipientDropdown from "../Drowdown";
import { dateFormatList } from "../../utils/constants";
import { DatePickerDropdownStyles, StyledDatePicker } from "../DatePicker";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { formatSharedDate } from "../../utils/dateFormatting";
import {
  getShareRecipients,
  shareDocument,
} from "../../slice/MatterDetailsSlice";
import { fetchTasks } from "../../service/home";
import { useDispatch } from "react-redux";
import ReminderBlock from "../ReminderBlock";
import MultilineTextField from "../TextField/MultilineTextField";
import { styled } from "@mui/material/styles";
import { ErrorIcon } from "../../assets/icons";
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
  weekdaysMin: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
});

const ErrorTextWrapper = styled("div")({
  color: "#C63939",
  fontSize: "12px",
  fontWeight: "400",
  display: "flex",
  alignItems: "center",
  marginTop: "4px",
  marginLeft: "0",
  gap: "6px",
});

const ShareModal = (props) => {
  const {
    open,
    onClose,
    selectedDoc,
    setdrawerError,
    setShowNotification,
    tabs = [],
    closeTab = () => {},
    closeDrawer = () => {},
    reviewers = [],
    setReviewers,
    handleRemindClick,
    remindStatus,
    setRemindStatus,
    remindInProgressIds = [],
    relatedTaskId: relatedTaskIdProp,
    matterId,
  } = props;
  const [isConfirm, setIsConfirm] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [dueDate, setDueDate] = useState(null);
  const [comment, setComment] = useState("");
  const [showError, setShowError] = useState(false);
  const [relatedTaskId, setRelatedTaskId] = useState(
    relatedTaskIdProp ? String(relatedTaskIdProp) : ""
  );
  const [relatedTasks, setRelatedTasks] = useState([]);

  const dispatch = useDispatch();

  const isTabOpen = tabs.some((tab) => tab.id === selectedDoc?.unique_id);

  const showReminder = remindStatus && !isConfirm;

  const handleConfirmClick = () => {
    if (selectedRecipients?.length === 0) {
      setShowError(true);
      return;
    }
    if (isTabOpen && !isConfirm) {
      setIsConfirm(true);
      return;
    }

    const payload = {
      document: selectedDoc?.unique_id?.substring(1),
      recipients: selectedRecipients.map((r) => r.id),
      notes: comment,
      due_date: dueDate
        ? dayjs(dueDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS")
        : null,
      ...(relatedTaskId
        ? { related_task_id: Number(relatedTaskId) }
        : {}),
    };

    dispatch(shareDocument(payload))
      .unwrap()
      .then((response) => {
        if (response?.status === 201) {
          const res = {
            id: Date.now().toString(),
            newMsg: `'${selectedDoc?.name}' has been shared successfully`,
            success: true,
          };
          setdrawerError([res]);
          setShowNotification(true);
          onClose();
          closeDrawer();
          if (isTabOpen) {
            closeTab(selectedDoc?.unique_id, true);
          }
        }
      })
      .catch((err) => {
        console.error("Error in fetching share modal recipients", err);
        if (err?.status !== 401) {
          const errorMessage =
            err?.status === 500
              ? "Internal server error"
              : err?.message || "Unable to share document";

          setdrawerError([
            {
              id: Date.now().toString(),
              newMsg: errorMessage,
              success: false,
            },
          ]);

          setShowNotification(true);
        }
      });
  };

  useEffect(() => {
    const docId = selectedDoc?.unique_id?.substring(1);
    if (docId) {
      dispatch(getShareRecipients({ docId }))
        .then((response) => {
          if (response.payload?.status === 200) {
            setRecipients(response?.payload?.data?.users);
            if (typeof setReviewers === "function") {
              setReviewers(response?.payload?.data?.existing_shares);
            }
          }
        })
        .catch((err) => {
          console.error("Error in fetching share modal recipients", err);
        });
    }
    return () => {
      setRecipients([]);
      if (typeof setRemindStatus === "function") {
        setRemindStatus(null);
      }
    };
  }, [selectedDoc?.unique_id, dispatch, setRecipients, setRemindStatus, setReviewers]);

  useEffect(() => {
    setRelatedTaskId(relatedTaskIdProp ? String(relatedTaskIdProp) : "");
  }, [relatedTaskIdProp]);

  useEffect(() => {
    if (!open || !matterId || relatedTaskIdProp) {
      return undefined;
    }
    let cancelled = false;
    fetchTasks({
      matter_id: matterId,
      role: "assigned_to_me",
      chip_status: "ACTIVE",
      page_size: 50,
    })
      .then((response) => {
        if (!cancelled) {
          setRelatedTasks(response?.data?.results || []);
        }
      })
      .catch(() => {
        if (!cancelled) setRelatedTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, matterId, relatedTaskIdProp]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        //Note: Confirmation modal does not close on backdrop click to avoid confusion
        if (isConfirm && reason === "backdropClick") return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      className="share-modal"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              fontFamily: "manrope",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              overflow: "hidden",
            }}
          >
            {isConfirm ? (
              "Close File to Share?"
            ) : (
              <>
                Share File Internally -{" "}
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#48566B",
                    maxWidth: "300px",
                    flex: 1,
                  }}
                >
                  {selectedDoc?.name || ""}
                </Box>
              </>
            )}
          </Typography>
          {!isConfirm && (
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontSize: "12px",
                color: "#5A6C87",
                fontWeight: "400",
              }}
            >
              Anyone with the file access can edit
            </Typography>
          )}
        </Box>
        <BackButton>
          <X size={20} onClick={onClose} />
        </BackButton>
      </DialogTitle>

      <DialogContent>
        {isConfirm ? (
          <Box
            sx={{
              fontSize: "14px",
              color: "#48566B",
              fontFamily: "Manrope",
              fontWeight: "500",
            }}
          >
            Sharing this file will close your current file tab and release the
            lock for reviewers to edit.
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 0.25 }}>
              <FormControl fullWidth error={selectedRecipients.length === 0}>
                <RecipientDropdown
                  selectedData={selectedRecipients}
                  setSelectedData={setSelectedRecipients}
                  setShowError={setShowError}
                  allRecipients={recipients}
                  error={selectedRecipients.length === 0 && showError}
                />
                {selectedRecipients.length === 0 && showError && (
                  <ErrorTextWrapper>
                    <ErrorIcon />
                    Please select at least one recipient.
                  </ErrorTextWrapper>
                )}
              </FormControl>
              <>
                <DatePickerDropdownStyles />
                <StyledDatePicker
                  format={dateFormatList}
                  getPopupContainer={() =>
                    document.querySelector(".share-modal")
                  }
                  onChange={(date) => setDueDate(date)}
                  allowClear={false}
                  inputReadOnly
                  placeholder="Due Date"
                  value={dueDate}
                  disabledDate={(current) => {
                    const today = dayjs().startOf("day");
                    const maxDate = dayjs().add(3, "year").endOf("day");
                    return current && (current < today || current > maxDate);
                  }}
                />
              </>
            </Box>

            <MultilineTextField
              value={comment}
              onChange={setComment}
              placeholder="Add note to review.."
            />

            {(relatedTaskIdProp || matterId) && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="related-task-label">Related task</InputLabel>
                <Select
                  labelId="related-task-label"
                  label="Related task"
                  value={relatedTaskId}
                  disabled={Boolean(relatedTaskIdProp)}
                  onChange={(event) => setRelatedTaskId(event.target.value)}
                >
                  {!relatedTaskIdProp && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}
                  {(relatedTaskIdProp
                    ? [{ id: relatedTaskIdProp, body: "This Task" }]
                    : relatedTasks
                  ).map((task) => (
                    <MenuItem key={task.id} value={String(task.id)}>
                      {(task.body || "Task").split("\n")[0]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {reviewers?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    pt: 2,
                    mt: 3,
                    mb: 1,
                    fontWeight: 600,
                    fontFamily: "manrope",
                    borderTop: "1px solid #F3F4F7",
                    fontSize: "16px",
                  }}
                >
                  Reviewers
                </Typography>
                <ReminderBlock
                  reviewers={reviewers}
                  remindInProgressIds={remindInProgressIds}
                  handleRemindClick={handleRemindClick}
                  formatSharedDate={formatSharedDate}
                  isModal={true}
                />
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: showReminder ? "space-between" : "flex-end",
          padding: "16px 24px",
          gap: "8px",
          borderTop: "1px solid #F3F4F7",
        }}
      >
        {showReminder && (
          <Typography
            sx={{
              fontFamily: "manrope",
              fontSize: "13px",
              fontWeight: 500,
              color: remindStatus.success ? "#128F4E" : "#E53935",
            }}
          >
            {remindStatus.success
              ? `Reminder sent to ${remindStatus.user}`
              : `Error in reminding ${remindStatus.user}`}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              fontFamily: "manrope",
              borderRadius: "6px",
              border: "1px solid #DDE2E9",
              padding: "10px 12px",
              height: "32px",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#F3F4F7",
              color: "#1B2028",
              fontSize: "13px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirmClick}
            disabled={!isConfirm && selectedRecipients.length === 0} //Commenting it would show validation messages
            sx={{
              fontFamily: "manrope",
              borderRadius: "6px",
              padding: "10px 12px",
              height: "32px",
              textTransform: "none",
              fontWeight: 600,
              color: "#FFFFFF",
              fontSize: "13px",
              background: "#0C53BB",
              "&.Mui-disabled": {
                background: "#0C53BB",
                color: "#FFFFFF",
                opacity: "40%",
              },
            }}
          >
            {isConfirm ? "Yes, Proceed" : "Confirm"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const ReviewerPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  status: PropTypes.string.isRequired,
  shared_at: PropTypes.string,
  can_remind: PropTypes.bool,
  shared_with: PropTypes.shape({
    full_name: PropTypes.string,
    username: PropTypes.string,
  }),
});

ShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDoc: PropTypes.shape({
    name: PropTypes.string.isRequired,
    unique_id: PropTypes.string.isRequired,
  }).isRequired,
  setdrawerError: PropTypes.func,
  setShowNotification: PropTypes.func,
  closeTab: PropTypes.func,
  closeDrawer: PropTypes.func,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  reviewers: PropTypes.arrayOf(ReviewerPropType),
  setReviewers: PropTypes.func,
  handleRemindClick: PropTypes.func,
  remindStatus: PropTypes.shape({
    user: PropTypes.string,
    success: PropTypes.bool,
  }),
  setRemindStatus: PropTypes.func,
  remindInProgressIds: PropTypes.arrayOf(PropTypes.string),
  relatedTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ShareModal;
